'use client'

import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import Image from 'next/image'

interface ExpertTalk {
  id: string
  title: string
  subtitle: string
  description: string
  image?: string
  icon?: string
}

export default function ExpertTalksSlider({ talks }: { talks: ExpertTalk[] }) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [cardsPerSlide, setCardsPerSlide] = useState(3)

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = (e: MediaQueryListEvent | MediaQueryList) => {
      setCardsPerSlide(e.matches ? 3 : 1)
      setCurrentSlide(0)
    }
    handler(mq)
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void)
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void)
  }, [])

  const totalSlides = Math.ceil(talks.length / cardsPerSlide)

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides)
  }

  return (
    <div className="relative">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold text-left">Expert Talks</h2>
        <div className="flex space-x-2">
          <button
            onClick={prevSlide}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            disabled={currentSlide === 0}
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={nextSlide}
            className="p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            disabled={currentSlide === totalSlides - 1}
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {Array.from({ length: totalSlides }).map((_, slideIndex) => (
            <div key={slideIndex} className="w-full flex-shrink-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {talks
                  .slice(slideIndex * cardsPerSlide, slideIndex * cardsPerSlide + cardsPerSlide)
                  .map((talk) => (
                    <Link
                      key={talk.id}
                      href={`/courses/${talk.id}`}
                      className="block"
                      aria-label={`Learn more about ${talk.title}`}
                    >
                      <article className="bg-[#111111] border border-gray-800 rounded-3xl overflow-hidden transition-all duration-300 hover:bg-[#141414] hover:border-gray-700 hover:scale-105 shadow-lg cursor-pointer">
                        <div className="relative">
                          <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative">
                            {talk.image ? (
                              <Image
                                src={talk.image}
                                alt={`${talk.title} - ${talk.description.substring(0, 50)}...`}
                                width={400}
                                height={225}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="text-6xl">{talk.icon}</div>
                            )}
                          </div>
                          <div className="p-6">
                            <header className="mb-4">
                              <h3 className="font-semibold text-lg text-white text-left">
                                {talk.subtitle}
                              </h3>
                            </header>
                            <div className="mb-6">
                              <p className="text-gray-400 text-sm text-left">{talk.description}</p>
                            </div>
                            <div className="flex justify-start">
                              <Button className="bg-white text-black hover:bg-white/90 py-2 px-4 rounded-full font-medium transition-colors text-sm inline-flex items-center gap-2">
                                View Course
                                <ArrowRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center mt-6 space-x-2">
        {Array.from({ length: totalSlides }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors ${
              currentSlide === index ? 'bg-white' : 'bg-gray-600'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
